import AddReactionIcon from '@mui/icons-material/EmojiEmotions';
import { Box, Button, Chip, IconButton, Popover, Typography } from '@mui/material';
import ReactionIcon from 'components/common/ReactionIcon';
import defaultReactionSet, { MAX_COMPANY_REACTIONS } from 'constants/reactions';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';

const loadPicker = () =>
  import('components/@home/@messages/ChatPanel/SendForm/buttons/PickEmojiButton/Picker').then(
    m => ({ Picker: m.default, data: m.data }),
  );

const PICKER_WIDTH = 352;
const PICKER_HEIGHT = 435;

const ReactionsConfig = () => {
  const { values, setFieldValue } = useFormikContext();
  const locale = useSelector(state => state.i18n.locale);
  const rawReactions = values.reactions;
  const selected = useMemo(
    () => (rawReactions?.length ? rawReactions : [...defaultReactionSet]),
    [rawReactions],
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerData, setPickerData] = useState(null);
  const pendingAnchor = useRef(null);

  useEffect(() => {
    if (pickerData && pendingAnchor.current) {
      setAnchorEl(pendingAnchor.current);
      pendingAnchor.current = null;
    }
  }, [pickerData]);

  const openPicker = useCallback(
    event => {
      const target = event.currentTarget;
      if (selected.length >= MAX_COMPANY_REACTIONS) return;
      if (pickerData) {
        setAnchorEl(target);
      } else {
        pendingAnchor.current = target;
        loadPicker().then(({ Picker, data }) => {
          setPickerData({ Picker, data });
        });
      }
    },
    [pickerData, selected.length],
  );

  const closePicker = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onEmojiSelect = useCallback(
    emoji => {
      if (!emoji?.native) return;
      const token = emoji.native.trim();
      if (!token || selected.includes(token) || selected.length >= MAX_COMPANY_REACTIONS) {
        closePicker();
        return;
      }
      setFieldValue('reactions', [...selected, token]);
      closePicker();
    },
    [closePicker, selected, setFieldValue],
  );

  const removeReaction = useCallback(
    token => () => {
      if (selected.length <= 1) return;
      setFieldValue(
        'reactions',
        selected.filter(r => r !== token),
      );
    },
    [selected, setFieldValue],
  );

  const addPreset = useCallback(
    name => () => {
      if (selected.includes(name) || selected.length >= MAX_COMPANY_REACTIONS) return;
      setFieldValue('reactions', [...selected, name]);
    },
    [selected, setFieldValue],
  );

  const PickerComponent = pickerData?.Picker;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
        }}
      >
        {I18n.t('CompanyForm.Reactions.Help')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1.5 }}>
        {selected.map(token => (
          <Chip
            key={token}
            label={<ReactionIcon name={token} />}
            onDelete={selected.length > 1 ? removeReaction(token) : undefined}
            size="small"
            sx={{ '& .MuiChip-label': { display: 'flex', alignItems: 'center', px: 0.5 } }}
          />
        ))}
        <IconButton
          size="small"
          color="primary"
          onClick={openPicker}
          disabled={selected.length >= MAX_COMPANY_REACTIONS}
          aria-label={I18n.t('CompanyForm.Reactions.AddEmoji')}
        >
          <AddReactionIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mb: 0.5,
        }}
      >
        {I18n.t('CompanyForm.Reactions.Presets')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {defaultReactionSet.map(name => {
          const already = selected.includes(name);
          return (
            <Button
              key={name}
              size="small"
              variant={already ? 'outlined' : 'text'}
              disabled={already || selected.length >= MAX_COMPANY_REACTIONS}
              onClick={addPreset(name)}
              sx={{ minWidth: 44, px: 0.5 }}
            >
              <ReactionIcon name={name} />
            </Button>
          );
        })}
      </Box>

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
        }}
      >
        {I18n.t('CompanyForm.Reactions.MinOne')}
        {' · '}
        {I18n.t('CompanyForm.Reactions.MaxCount', { count: MAX_COMPANY_REACTIONS })}
      </Typography>

      <Popover
        open={Boolean(anchorEl) && Boolean(pickerData)}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: { width: PICKER_WIDTH, height: PICKER_HEIGHT, overflow: 'hidden' },
          },
        }}
      >
        {PickerComponent && pickerData?.data ? (
          <PickerComponent
            data={pickerData.data}
            onEmojiSelect={onEmojiSelect}
            locale={locale}
            previewPosition="none"
            skinTonePosition="none"
          />
        ) : null}
      </Popover>
    </Box>
  );
};

export default ReactionsConfig;
